<template>
  <div>
    <v-combobox prepend-icon="mdi-clock-time-seven-outline" :label="$t('SingleTimeDropdown.Time')"
      v-model="selectedTime" :items="timesList" menu-props="auto" item-text="timeDisplay" item-value="timeValue"
      :readonly="readonly" :disabled="disabled" :placeholder="placeholder" :append-icon="readonly ? '' : 'mdi-menu-down'"
      variant="underlined" @change="onUpdateSelectedTime">
    </v-combobox>
    <div v-if="errors.isTimeInvalid" class="error-msg">{{ $t('SingleTimeDropdown.TimeInvalid') }}</div>
  </div>
</template>

<script>
import moment from "moment/moment";

export default {
  components: {},
  name: 'SingleTimeDropdown',
  props: {
    time: String,
    readonly: Boolean,
    disabled: Boolean,
    placeholder: String,
    round: Boolean
  },
  data() {
    return {
      errors: {
        isTimeInvalid: false,
      },
      selectedTime: null,
      timesList: [],
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    isAmPmFormat() {
      const format = this.facility.timeFormat.split(' ')[1] || ''
      return format === 'A'
    },
    timeFormat() {
      return this.isAmPmFormat ? 'hh:mm A' : 'HH:mm'
    }
  },

  watch: {
    selectedTime(value) {
      this.isTimeValid()
      this.emitChanges()
    }
  },

  created() {
    const _time = this.time ? this.time : moment().format(this.timeFormat)

    if (this.round) {
      this.selectedTime = this.roundTo15Minutes(_time)
    }
    else {
      this.selectedTime = _time
    }

    this.timesList = this.generateTimesForTimeList(this.roundTo15Minutes(_time))
  },

  methods: {
    generateTimesForTimeList(time) {
      return this.generateTimesList(time, this.isAmPmFormat);
    },

    generateTimesList(time, isAmPm) {
      const times = [];
      const [hours, mins] = time.split(':');
      const minutes = mins.split(' ')[0];
      const amPm = time.includes('AM') ? 'AM' : 'PM'

      let hoursNumber = Number(hours);
      if (isAmPm) {
        if (time.includes('AM') && hoursNumber === 12) {
          hoursNumber -= 12;
        } else if (time.includes('PM') && hoursNumber < 12) {
          hoursNumber += 12;
        }
      }

      const timeMinutes = hoursNumber * 60 + Number(minutes);

      for (let i = 0; i < 97; i++) {
        const timeMinutes = i * 15;
        const generatedHours = Math.floor(timeMinutes / 60);
        const generatedMinutes = (timeMinutes % 60).toString().padStart(2, '0');

        let time;
        if (isAmPm) {
          const amPm = generatedHours < 12 ? 'AM' : 'PM';
          const formattedHours = (generatedHours % 12 || 12).toString().padStart(2, '0');
          time = `${formattedHours}:${generatedMinutes} ${amPm}`
        } else {
          const formattedHours = generatedHours.toString().padStart(2, '0');
          time = `${formattedHours}:${generatedMinutes}`
        }

        const timeDiffMinutes = timeMinutes - timeMinutes;

        let timeDiff;
        if (timeDiffMinutes <= 0) {
          timeDiff = null;
        } else {
          const timeDiffHours = Math.floor(timeDiffMinutes / 60);
          const timeDiffMinutesRemainder = timeDiffMinutes % 60;
          timeDiff = `${timeDiffHours.toString().padStart(2, '0')}:${timeDiffMinutesRemainder.toString().padStart(2, '0')}`
        }

        times.push({
          timeValue: time,
          timeDisplay: time,
          timeDiff: timeDiff
        });
      }

      return times;
    },

    roundTo15Minutes(time) {
      const timeComponents = time.split(/:|(?=[ap]m)/i);
      const hours = parseInt(timeComponents[0], 10);
      const minutes = parseInt(timeComponents[1], 10);
      const amPm = timeComponents[2];

      // Round the minutes to the nearest 15-minute interval
      let roundedMinutes = Math.ceil(minutes / 15) * 15;
      let roundedHours = hours;

      if (roundedMinutes === 60) {
        roundedHours += 1;
        roundedMinutes = 0;
      }

      // Check if AM/PM format is present
      if (amPm) {
        if (amPm.toLowerCase() === 'PM' && roundedHours < 12) {
          roundedHours += 12;
        } else if (amPm.toLowerCase() === 'AM' && roundedHours === 12) {
          roundedHours = 0;
        }
      }

      // Format the hours and minutes
      const formattedHours = roundedHours.toString().padStart(2, '0');
      const formattedMinutes = roundedMinutes.toString().padStart(2, '0');

      // Determine the format based on the presence of AM/PM
      if (amPm) {
        return `${formattedHours}:${formattedMinutes} ${amPm}`;
      } else {
        return `${formattedHours}:${formattedMinutes}`;
      }
    },

    onUpdateSelectedTime(selectedItem) {
      //set value of a selected item or value that user manually entered
      this.selectedTime = selectedItem?.timeValue || this.fixTime(selectedItem) || null;

      if (!this.isTimeValid()) {
        this.selectedTime = null
        return
      }

      if (!this.selectedTime && this.round) {
        this.selectedTime = this.roundTo15Minutes(this.selectedTime)
      }

    },

    isTimeValid() {
      if (!moment(this.selectedTime, this.timeFormat, true).isValid()) {
        this.errors.isTimeInvalid = true
        return false
      }

      this.errors.isTimeInvalid = false

      return true
    },

    //fix manually entered time
    fixTime(time) {
      if (!time) return null

      const timeParts = time.split(' ');
      const timeWithoutSpace = timeParts.join('');

      if (timeWithoutSpace.match(/^\d{1,2}:\d{2}(AM|PM)$/i)) {
        const formattedTime = time.replace(/(\d{1,2}:\d{2})(AM|PM)/i, '$1 $2');
        return formattedTime;
      }

      return time;
    },

    async emitChanges() {
      this.$emit('onTimeChange', { time: this.selectedTime, isValid: this.isTimeValid() })
    }
  }
}
</script>

<style lang="scss" scoped></style>
